// NATIVE
import { useEffect, useState } from 'react';

// INSTALLED
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// CUSTOM
import { Form, Formik } from 'formik';
import { timeToClosedToast } from '../../commons';
import { SnackAlert } from '../../components/SnackAlert';
import { closeSessionGlobal } from '../../commons/utils';
import { IUserPermissions } from '../../commons/interfaces';
import MultipleSnackbars from '../../components/MultipleSnackbars';
import { storeDetailEditUserPermission } from '../../store/users_permissions';
import { ModalSessionExpired } from '../../components/generics/ModalSessionExpired';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import { EditUserSystemPermissions } from '../../components/generics/EditUserSystemPermissions';
import { EditCardUserSystemDetails } from '../../components/generics/EditCardUserSystemDetails';
import formInitialValues from '../../components/usersPermissions/formModels/editar/formInitialValues';
import editUsersPemissionsFormModel from '../../components/usersPermissions/formModels/editar/editUsersPemissionsFormModel';

/**
 * EditUserPermissions component is responsible for rendering the user interface
 * for editing user permissions. It utilizes various hooks and components to manage
 * state, handle form submission, and display alerts and modals.
 *
 * @component
 *
 * @example
 * return (
 *   <EditUserPermissions />
 * )
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component uses the following hooks:
 * - `useDispatch` to dispatch actions to the Redux store.
 * - `useNavigate` to navigate to different routes.
 * - `useSelector` to select state from the Redux store.
 * - `useState` to manage local state.
 * - `useEffect` to perform side effects.
 *
 * The component also includes the following state variables:
 * - `userWithPermissions` to store the user permissions data.
 * - `timeAlert` to manage the auto-close time for alerts.
 * - `messagesAlerts` to store alert messages.
 * - `modalSessionExpired` to manage the session expired modal visibility.
 * - `alert_snack` to manage the visibility of the snack alert.
 * - `msgSnack` to store the message and status for the snack alert.
 *
 * The component includes the following functions:
 * - `_handleSubmit` to handle form submission.
 * - `validateResponse` to validate the response from the server.
 *
 * The component renders the following child components:
 * - `ModalSessionExpired` to display a session expired modal.
 * - `MultipleSnackbars` to display multiple snackbars for alerts.
 * - `SnackAlert` to display a single snack alert.
 * - `BreadCrumbsAndTitle` to display breadcrumbs and the title.
 * - `Formik` to handle form state and validation.
 * - `EditCardUserSystemDetails` to display user system details.
 * - `EditUserSystemPermissions` to display user system permissions.
 */
export const EditUserPermissions = () => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // USE NAVIGATE
  const navigate = useNavigate();

  // USE SELECTOR
  const { user_with_permissions }: any = useSelector((state: any) => state.usersPermissions);

  // DATA MODEL
  const { formId, formField } = editUsersPemissionsFormModel;

  // USE STATE
  const [userWithPermissions, setUserWithPermissions] = useState<IUserPermissions>({
    id: 'Cargando...',
    name: 'Cargando...',
    email: 'Cargando...',
    avatar: 'Cargando...',
    status: 'Cargando...',
    permissions: [],
  });

  const [timeAlert, setTimeAlert] = useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = useState<any>([]);
  const [modalSessionExpired, setModalSessionExpired] = useState<boolean>(false);

  const [alert_snack, setAlertSnack] = useState(false);
  const [msgSnack, setMsgSnack] = useState({
    msg: '',
    status: 'success',
  });

  // USE EFFECT
  useEffect(() => {
    if (!user_with_permissions || Object.keys(user_with_permissions).length == 0) {
      navigate('/manager/usuarios-permisos');
    }
  }, []);

  useEffect(() => {
    setUserWithPermissions(user_with_permissions);
  }, [user_with_permissions]);

  //  CONST FUNCTION
  const _handleSubmit = async (values: any, actions: any) => {
    actions.setSubmitting(true);

    const response = await dispatch(storeDetailEditUserPermission(values));

    const result = await validateResponse(response, ' (Editar Usuario Permiso) ');

    if (!result) {
      return false;
    }

    const message: any = {
      msg: 'Permisos editados con exitos!',
      status: 'success',
    };

    await setMsgSnack(message);
    await setAlertSnack(true);

    setTimeout(() => {
      navigate('/manager/usuarios-permisos');
    }, 1500);
  };

  const validateResponse = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    return true;
  };

  return (
    <>
      <ModalSessionExpired open={modalSessionExpired} />
      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />

      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BreadCrumbsAndTitle
        title="Editar Usuario Permisos"
        mainModule="configuracion"
        parent="Listar Usuarios Permisos"
        action="/manager/usuarios-permisos"
      />

      <Formik initialValues={formInitialValues} onSubmit={_handleSubmit}>
        <Form id={formId}>
          <Grid container spacing={2}>
            {/* INICIO LADO A */}
            <Grid size={3}>
              <EditCardUserSystemDetails {...userWithPermissions} />
            </Grid>
            {/* FIN LADO A  */}

            {/* INICIO LADO B */}
            <Grid size={9}>
              <EditUserSystemPermissions {...userWithPermissions} />
            </Grid>
            {/* FIN LADO B  */}
          </Grid>
        </Form>
      </Formik>
    </>
  );
};
